import { createAsyncThunk } from '@reduxjs/toolkit';
import { setSelectedProjectId } from 'scenes/Project/store/actions/projectActions';
import { parseBankLocationQuery } from 'scenes/ProjectBank/utils/bankLocation';
import { findProjectRootFolder } from 'scenes/ProjectBank/utils/projectRootFolder';
import { setInitialProjectBankPath } from 'store/filepermissions/actions/filepermissions.actions';
import { loadProjectMediaTagsData } from 'store/mediaTags/thunks/loadProjectMediaTagsData.thunk';
import { selectProjectFolderFilePermissionsArray } from '../selectors/project-bank.selectors';
import { loadProjectBankFormsFolderContents } from './loadProjectBankFormsFolderContents.thunk';
import { loadProjectFolders } from './loadProjectFolders.thunk';
import { loadPrivateProjectBankItemsOfProject } from './private-project-bank/loadPrivateProjectBankItemsOfProject.thunk';

/**
 * Opens the bank of a project: loads its folders and its private items and
 * roots the tree at the project's own root folder. What the URL asks for
 * (utils/bankLocation) is opened by the scene's URL sync once
 * `isInitialised` in the slice turns on, which happens when this settles —
 * unless a later init has meanwhile taken the slice (a quick hop to another
 * bank, or the bank closed and opened again), in which case this one is
 * `stale`. The URL is read here only for the older `?path=` form, the
 * tree's root when the project's own root folder was not loaded.
 *
 * @param {{projectId: string, query?: Object, path?: string}} params
 *   query: parseBankLocationQuery's result for the scene's URL (`path` is
 *   the older form, a FilePermission path)
 */
export const initProjectBank = createAsyncThunk(
  'projectBank/initProjectBank',
  async (params, { dispatch, getState, requestId }) => {
    const { projectId } = params;
    const isStale = () => getState().projectBank.initRequestId !== requestId;
    const query =
      params.query ||
      parseBankLocationQuery(
        params.path ? `?path=${encodeURIComponent(params.path)}` : ''
      );

    dispatch(setSelectedProjectId(projectId));
    if (projectId) {
      dispatch(loadProjectMediaTagsData());
    }

    // The tilaajavastuu documents and the business's integration status are
    // loaded by the scene once the permissions and the project entity are at
    // hand (ProjectBank.js): on a fresh page load both arrive after this.
    await Promise.all([
      dispatch(loadPrivateProjectBankItemsOfProject()),
      dispatch(loadProjectFolders()),
    ]);

    if (isStale()) {
      return { stale: true, projectId };
    }

    // The route project's forms, not waited for; a link into a forms folder
    // has the URL sync load that project's forms once the context is here.
    dispatch(loadProjectBankFormsFolderContents(projectId));

    // The tree is rooted at the project's own root folder whatever folder
    // the link names; the older ?path form is the root only when no root
    // folder was loaded (a link into a bank this user cannot see).
    const rootFolder = findProjectRootFolder(
      selectProjectFolderFilePermissionsArray(getState()),
      projectId
    );
    dispatch(setInitialProjectBankPath(rootFolder?.path || query.path || ''));

    return { stale: false, projectId };
  }
);
